import { TABLE_PREFERENCES, type TableLayout, type TablePreferencesStore } from '@acme/ui'
import { onBeforeUnmount, provide, reactive, watch } from 'vue'

export interface SavedTableLayout extends TableLayout {
  tableId: string
}

// The API of the person's table layouts (portal: per institution; console: the platform scope).
export interface TablePreferencesApi {
  load: (scope: string, signal: AbortSignal) => Promise<readonly SavedTableLayout[]>
  save: (scope: string, tableId: string, layout: TableLayout) => Promise<unknown>
  remove: (scope: string, tableId: string) => Promise<unknown>
}

const SAVE_DELAY_MS = 600

// Provides the layouts of UiDataTable for the screens below (call it in the app layout). A change shows
// at once and is saved a moment later (dragging several columns makes one request). If saving fails,
// the layout stays for this visit and comes back from the server on the next one.
export function provideTablePreferences(scope: () => string | undefined, api: TablePreferencesApi): TablePreferencesStore {
  const layouts = reactive(new Map<string, TableLayout>())
  const pending = new Map<string, { timer: ReturnType<typeof setTimeout>; run: () => void }>()
  let loading: AbortController | null = null

  watch(
    scope,
    (current) => {
      loading?.abort()
      layouts.clear()
      if (!current) return
      const controller = new AbortController()
      loading = controller
      api
        .load(current, controller.signal)
        .then((items) => {
          if (controller.signal.aborted) return
          for (const item of items) {
            // A change made while loading wins.
            if (!pending.has(item.tableId)) layouts.set(item.tableId, { order: [...item.order], hidden: [...item.hidden], version: item.version ?? 1 })
          }
        })
        .catch(() => {
          // Without the saved layouts the tables show the default columns.
        })
    },
    { immediate: true },
  )

  // Leaving (logout, another app) sends what is still waiting.
  onBeforeUnmount(() => {
    loading?.abort()
    pending.forEach(({ timer, run }) => {
      clearTimeout(timer)
      run()
    })
  })

  const store: TablePreferencesStore = {
    scope: () => scope() ?? 'none',
    layout: (tableId) => layouts.get(tableId),
    save: (tableId, layout) => {
      const current = scope()
      if (layout) layouts.set(tableId, layout)
      else layouts.delete(tableId)
      if (!current) return
      const previous = pending.get(tableId)
      if (previous) clearTimeout(previous.timer)
      const run = () => {
        pending.delete(tableId)
        const request = layout ? api.save(current, tableId, layout) : api.remove(current, tableId)
        request.catch(() => {
          // Kept for this visit; the next load brings back what the server has.
        })
      }
      pending.set(tableId, { timer: setTimeout(run, SAVE_DELAY_MS), run })
    },
  }
  provide(TABLE_PREFERENCES, store)
  return store
}
